/** Game audio: HTMLAudio assets + tiny Web Audio beeps. */

import { assetUrl } from "../assetUrl";

const VOLUME_KEY = "cowboy_volume";

/** Relative levels before master volume is applied. */
const VOL = {
  menu: 0.45,
  wind: 0.7,
  footstep: 0.7,
  gunshot: 0.7,
  bell: 0.7,
  countdown: 0.7,
  whip: 0.5,
  hurt: 0.7,
  hiss: 0.75,
  inflate: 0.7,
  pop: 0.7,
  result: 0.7,
} as const;

let masterVolume = loadMasterVolume();

function loadMasterVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw === null) return 0.5;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0.5;
    return Math.max(0, Math.min(1, n));
  } catch {
    return 0.5;
  }
}

export function getMasterVolume(): number {
  return masterVolume;
}

/** 0–1 master gain. Persists and updates currently playing loops. */
export function setMasterVolume(v: number): void {
  masterVolume = Math.max(0, Math.min(1, v));
  try {
    localStorage.setItem(VOLUME_KEY, String(masterVolume));
  } catch {
    /* ignore quota / private mode */
  }
  applyMasterToPlaying();
}

function scaled(base: number): number {
  return base * masterVolume;
}

function applyMasterToPlaying(): void {
  if (menuMusic) menuMusic.volume = scaled(VOL.menu);
  if (windAmbient) windAmbient.volume = scaled(VOL.wind);
  if (gunshot) gunshot.volume = scaled(VOL.gunshot);
  if (hurt) hurt.volume = scaled(VOL.hurt);
  if (hiss) hiss.volume = scaled(VOL.hiss);
  if (whip) whip.volume = scaled(VOL.whip);
  if (bell) bell.volume = scaled(VOL.bell);
  if (countdown) countdown.volume = scaled(VOL.countdown);
  if (inflate) inflate.volume = scaled(VOL.inflate);
  if (pop) pop.volume = scaled(VOL.pop);
  if (resultTheme) resultTheme.volume = scaled(VOL.result);
  for (const s of footsteps) {
    if (s) s.volume = scaled(VOL.footstep);
  }
}

let ctx: AudioContext | null = null;
let menuMusic: HTMLAudioElement | null = null;
let windAmbient: HTMLAudioElement | null = null;
let gunshot: HTMLAudioElement | null = null;
let hurt: HTMLAudioElement | null = null;
let hiss: HTMLAudioElement | null = null;
let whip: HTMLAudioElement | null = null;
let bell: HTMLAudioElement | null = null;
let countdown: HTMLAudioElement | null = null;
let inflate: HTMLAudioElement | null = null;
let flashbangSfx: HTMLAudioElement | null = null;
const footsteps: Array<HTMLAudioElement | null> = [null, null];

function ac(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

function beep(
  freq: number,
  duration: number,
  type: OscillatorType = "square",
  gain = 0.08
): void {
  try {
    const g0 = scaled(gain);
    if (g0 <= 0) return;
    const audio = ac();
    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.value = g0;
    g.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + duration);
    osc.connect(g);
    g.connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + duration);
  } catch {
    /* autoplay restrictions */
  }
}

export function resumeAudio(): void {
  try {
    void ac().resume();
  } catch {
    /* ignore */
  }
}

/** Looping title / lobby track. Starts after a user gesture (autoplay policy). */
export function startMenuMusic(): void {
  stopWindAmbient();
  stopResultTheme();
  stopCountdown();
  try {
    if (!menuMusic) {
      menuMusic = new Audio(assetUrl("music/we_ride_at_dawn.ogg"));
      menuMusic.loop = true;
      menuMusic.preload = "auto";
    }
    menuMusic.volume = scaled(VOL.menu);
    if (menuMusic.paused) {
      void menuMusic.play().catch(() => {
        /* wait for a later gesture */
      });
    }
  } catch {
    /* ignore */
  }
}

export function stopMenuMusic(): void {
  if (!menuMusic) return;
  menuMusic.pause();
  menuMusic.currentTime = 0;
  startWindAmbient();
}

/** Desert wind loop during the duel (after menu music stops). */
export function startWindAmbient(): void {
  try {
    if (!windAmbient) {
      windAmbient = new Audio(assetUrl("sounds/wind.ogg"));
      windAmbient.loop = true;
      windAmbient.preload = "auto";
    }
    windAmbient.volume = scaled(VOL.wind);
    if (windAmbient.paused) {
      void windAmbient.play().catch(() => {
        /* autoplay restrictions */
      });
    }
  } catch {
    /* ignore */
  }
}

export function stopWindAmbient(): void {
  if (!windAmbient) return;
  windAmbient.pause();
  windAmbient.currentTime = 0;
}

/** Play alternating recorded footsteps: odd paces use 1, even paces use 2. */
export function playFootstep(pace: number): void {
  try {
    const index = Math.abs(pace - 1) % 2;
    let sound = footsteps[index];
    if (!sound) {
      sound = new Audio(assetUrl(`sounds/steps_${index + 1}.ogg`));
      sound.preload = "auto";
      footsteps[index] = sound;
    }
    sound.volume = scaled(VOL.footstep);
    sound.currentTime = 0;
    void sound.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Maneuver countdown ticks — one full track at the start of the pace-off. */
export function playCountdown(): void {
  try {
    stopCountdown();
    if (!countdown) {
      countdown = new Audio(assetUrl("sounds/countdown.ogg"));
      countdown.preload = "auto";
    }
    countdown.volume = scaled(VOL.countdown);
    countdown.currentTime = 0;
    void countdown.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

export function pauseCountdown(): void {
  if (!countdown || countdown.paused) return;
  countdown.pause();
}

export function resumeCountdown(): void {
  if (!countdown || !countdown.paused) return;
  if (countdown.ended) return;
  void countdown.play().catch(() => {
    /* autoplay restrictions */
  });
}

export function stopCountdown(): void {
  if (!countdown) return;
  countdown.pause();
  countdown.currentTime = 0;
}

export function playDrawFanfare(): void {
  beep(660, 0.12, "sawtooth", 0.1);
  setTimeout(() => beep(880, 0.18, "sawtooth", 0.1), 80);
}

export function playBang(volumeScale = 1): void {
  try {
    if (!gunshot) {
      gunshot = new Audio(assetUrl("sounds/gunshot.ogg"));
      gunshot.preload = "auto";
    }
    gunshot.volume = scaled(VOL.gunshot);
    // Clone so overlapping shots (both players) don't cut each other off
    const shot = gunshot.cloneNode(true) as HTMLAudioElement;
    shot.volume = Math.max(0, Math.min(1, gunshot.volume * volumeScale));
    void shot.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Bell at the start of the countdown (end of the hold phase). */
export function playBell(): void {
  try {
    if (!bell) {
      bell = new Audio(assetUrl("sounds/bell.ogg"));
      bell.preload = "auto";
    }
    bell.volume = scaled(VOL.bell);
    const sfx = bell.cloneNode(true) as HTMLAudioElement;
    sfx.volume = bell.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Whip crack at the end of the countdown (draw!). */
export function playWhip(): void {
  try {
    if (!whip) {
      whip = new Audio(assetUrl("sounds/whip.ogg"));
      whip.preload = "auto";
    }
    whip.volume = scaled(VOL.whip);
    const sfx = whip.cloneNode(true) as HTMLAudioElement;
    sfx.volume = whip.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

export function playHurt(): void {
  try {
    if (!hurt) {
      hurt = new Audio(assetUrl("sounds/hurt.ogg"));
      hurt.preload = "auto";
    }
    hurt.volume = scaled(VOL.hurt);
    const sfx = hurt.cloneNode(true) as HTMLAudioElement;
    sfx.volume = hurt.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Snake bite. */
export function playHiss(): void {
  try {
    if (!hiss) {
      hiss = new Audio(assetUrl("sounds/hiss.ogg"));
      hiss.preload = "auto";
    }
    hiss.volume = scaled(VOL.hiss);
    const sfx = hiss.cloneNode(true) as HTMLAudioElement;
    sfx.volume = hiss.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Local Cowboy Dummy inflate — only for the player who presses Space. */
export function playInflate(): void {
  try {
    if (!inflate) {
      inflate = new Audio(assetUrl("sounds/inflate.ogg"));
      inflate.preload = "auto";
    }
    inflate.volume = scaled(VOL.inflate);
    const sfx = inflate.cloneNode(true) as HTMLAudioElement;
    sfx.volume = inflate.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** Flashbang detonation. `volume` is 0–1 (victim full, thrower quieter). */
export function playFlashbang(volume: number): void {
  try {
    if (!flashbangSfx) {
      flashbangSfx = new Audio(assetUrl("sounds/flashbang.ogg"));
      flashbangSfx.preload = "auto";
    }
    const sfx = flashbangSfx.cloneNode(true) as HTMLAudioElement;
    sfx.volume = scaled(Math.max(0, Math.min(1, volume)));
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

let pop: HTMLAudioElement | null = null;
let resultTheme: HTMLAudioElement | null = null;

/** Cowboy Dummy bursting when shot. */
export function playPop(): void {
  try {
    if (!pop) {
      pop = new Audio(assetUrl("sounds/pop.ogg"));
      pop.preload = "auto";
    }
    pop.volume = scaled(VOL.pop);
    const sfx = pop.cloneNode(true) as HTMLAudioElement;
    sfx.volume = pop.volume;
    void sfx.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

/** One-shot end-of-match sting. Tie uses the losing theme. */
export function playResultTheme(won: boolean): void {
  try {
    // Stop menu track without kicking on wind (stopMenuMusic starts ambient).
    if (menuMusic) {
      menuMusic.pause();
      menuMusic.currentTime = 0;
    }
    stopWindAmbient();
    if (resultTheme) {
      resultTheme.pause();
      resultTheme = null;
    }
    resultTheme = new Audio(
      won
        ? assetUrl("sounds/winning_theme.ogg")
        : assetUrl("sounds/losing_theme.ogg")
    );
    resultTheme.loop = false;
    resultTheme.preload = "auto";
    resultTheme.volume = scaled(VOL.result);
    void resultTheme.play().catch(() => {
      /* autoplay restrictions */
    });
  } catch {
    /* ignore */
  }
}

export function stopResultTheme(): void {
  if (!resultTheme) return;
  resultTheme.pause();
  resultTheme = null;
}
